import React, {useCallback} from 'react';
import I18n from 'app/i18n';
import {isGuest} from 'app/utils';
import {
    EXCHANGE_MIN_POINTS,
    EXCHANGE_SCALE_MAX,
    EXCHANGE_SLIDER_TICKS,
    EXCHANGE_STEP,
    activeTickPointsFor,
    formatPointsAmount,
    percentOfScale
} from 'app/utils/shopPointsExchange';
import type ExchangeTierInterface from 'app/interfaces/shop/ExchangeTierInterface';

interface Props {
    balance: number;
    onChange: (points: number) => void;
    selectedPoints: number;
}

interface TickProps {
    isActive: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    percent: number;
    tier: ExchangeTierInterface;
}

const centeredPosition = (percent: number): React.CSSProperties => ({
    left: `${percent}%`,
    transform: 'translateX(-50%)'
});

const ExchangeSliderTick = ({isActive, onClick, percent, tier}: TickProps): React.ReactElement => {
    const className = `shop-exchange-slider__tick ${isActive ? 'shop-exchange-slider__tick_active' : ''}`;

    return <button
        className={className}
        onClick={onClick}
        style={centeredPosition(percent)}
        type="button"
        value={tier.points}
    >
        <span className="shop-exchange-slider__tick-mark" />
        <span className="shop-exchange-slider__tick-bonus">{`+${tier.bonusPercent}%`}</span>
        <span className="shop-exchange-slider__tick-points">{formatPointsAmount(tier.points)}</span>
    </button>;
};

const PointsExchangeSlider = ({balance, onChange, selectedPoints}: Props): React.ReactElement => {
    const guest = isGuest();
    const selectedPercent = percentOfScale(selectedPoints);
    const balancePercent = percentOfScale(Math.min(balance, EXCHANGE_SCALE_MAX));
    const fillPercent = guest
        ? selectedPercent
        : Math.min(selectedPercent, balancePercent);
    const overflowPercent = guest
        ? 0
        : Math.max(selectedPercent - balancePercent, 0);
    const activeTickPoints = activeTickPointsFor(selectedPoints);
    const isBalanceExceeded = !guest && selectedPoints > balance;
    const fillClassName = `shop-exchange-slider__fill ${overflowPercent > 0 ? 'shop-exchange-slider__fill_flush' : ''}`;
    const inputClassName = `shop-exchange-slider__input ${isBalanceExceeded ? 'shop-exchange-slider__input_exceeded' : ''}`;
    const balanceMarkClassName = `shop-exchange-slider__balance-mark ${isBalanceExceeded ? 'shop-exchange-slider__balance-mark_exceeded' : ''}`;

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(Number(e.target.value));
    }, [onChange]);

    const handleTickClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        onChange(Number(e.currentTarget.value));
    }, [onChange]);

    return <div className="shop-exchange-slider">
        <div className="shop-exchange-slider__body">
            <div className="shop-exchange-slider__scale">
                <div
                    className="shop-exchange-slider__balance"
                    style={centeredPosition(balancePercent)}
                >
                    {I18n.t('shop.exchange.your_balance', {amount: formatPointsAmount(balance)})}
                </div>
            </div>
            <div className="shop-exchange-slider__track-wrap">
                <div className="shop-exchange-slider__track">
                    <div className="shop-exchange-slider__locked" style={{left: `${balancePercent}%`}} />
                    <div
                        className={fillClassName}
                        style={{width: `${fillPercent}%`}}
                    />
                    {overflowPercent > 0 && <div
                        className="shop-exchange-slider__overflow"
                        style={{left: `${balancePercent}%`, width: `${overflowPercent}%`}}
                    />}
                </div>
                <div className="shop-exchange-slider__marks">
                    <div
                        className={balanceMarkClassName}
                        style={{left: `${balancePercent}%`}}
                    />
                </div>
                <input
                    className={inputClassName}
                    max={EXCHANGE_SCALE_MAX}
                    min={EXCHANGE_MIN_POINTS}
                    onChange={handleChange}
                    step={EXCHANGE_STEP}
                    type="range"
                    value={selectedPoints}
                />
                <div className="shop-exchange-slider__ticks">
                    {EXCHANGE_SLIDER_TICKS.map((tier: ExchangeTierInterface) => <ExchangeSliderTick
                        isActive={tier.points === activeTickPoints}
                        key={tier.points}
                        onClick={handleTickClick}
                        percent={percentOfScale(tier.points)}
                        tier={tier}
                    />)}
                </div>
            </div>
        </div>
    </div>;
};

export default PointsExchangeSlider;
